import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useQuery
} from '@tanstack/react-query';

import { CommentsTypeTypeEnum } from '@schema/cart';

import API from '@api';
import {
  ELEMENTS_COMMENTS,
  ELEMENTS_REVIEWS
} from '@constants/ElementsLimit';
import { withSsrCache } from '../../ssrCache';

/**
 * Комментарии/отзывы к товару. Общий кэш для модалки быстрого просмотра,
 * PeopleReviewsModal, BarMenuProduct и (в будущем) ProductPage.
 */
const PRODUCT_COMMENTS_STALE_TIME = 5 * 60 * 1000;
const PRODUCT_COMMENTS_GC_TIME = 10 * 60 * 1000;

export const PRODUCT_COMMENTS_QUERY_KEY = 'productComments';

export const productCommentsQueryOptions = (
  params: Parameters<typeof API.getCommentsProduct>[0]
) =>
  queryOptions({
    queryKey: [PRODUCT_COMMENTS_QUERY_KEY, params],
    queryFn: () => API.getCommentsProduct(params),
    select: res => res.getProductCommentsById,
    staleTime: PRODUCT_COMMENTS_STALE_TIME,
    gcTime: PRODUCT_COMMENTS_GC_TIME
  });

/** Отзывы конкретного покупателя (PeopleReviewsModal), кнопка «Показать ещё» */
export const PEOPLE_PRODUCT_COMMENTS_LIMIT = 8;

export const peopleProductCommentsInfiniteQueryOptions = (params: {
  peopleId: number;
  mainRubricId: number;
}) =>
  infiniteQueryOptions({
    queryKey: [PRODUCT_COMMENTS_QUERY_KEY, 'people', params],
    queryFn: ({ pageParam }) =>
      API.getCommentsProduct({
        setLimit: PEOPLE_PRODUCT_COMMENTS_LIMIT,
        page: pageParam,
        ...params
      }).then(res => res.getProductCommentsById),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPageParam * PEOPLE_PRODUCT_COMMENTS_LIMIT <
      (lastPage.totalCount || 0)
        ? lastPageParam + 1
        : undefined,
    // при смене рубрики виден прежний список, пока грузится новый (как раньше)
    placeholderData: keepPreviousData,
    staleTime: PRODUCT_COMMENTS_STALE_TIME,
    gcTime: PRODUCT_COMMENTS_GC_TIME
  });

/**
 * ЭТАП 2 — счётчики отзывов/вопросов как именованные фабрики: ЕДИНЫЙ источник
 * параметров для клиента (useProductCommentsCountsQuery) и SSR-префетча
 * /product/:id (ssrPrefetch) — ключи обязаны совпасть байт-в-байт.
 */
export const productReviewsCountQueryOptions = (catalogId: number) =>
  withSsrCache(
    productCommentsQueryOptions({
      catalogId,
      commentType: CommentsTypeTypeEnum.Review,
      blocksFilter: ['totalCount'],
      setLimit: ELEMENTS_REVIEWS
    }),
    'product'
  );

export const productQuestionsCountQueryOptions = (catalogId: number) =>
  withSsrCache(
    productCommentsQueryOptions({
      catalogId,
      commentType: CommentsTypeTypeEnum.Comment,
      blocksFilter: ['totalCount'],
      setLimit: ELEMENTS_COMMENTS
    }),
    'product'
  );

/** Есть ли фото в отзывах товара (фильтр «с фото») */
export const productHasAttachmentInCommentsQueryOptions = (
  catalogId: number
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['productHasAttachmentInComments', catalogId],
      queryFn: () => API.getIsProductHasAttachmentInComments({ catalogId }),
      select: res => !!res.isProductHasAttachmentInComments,
      staleTime: PRODUCT_COMMENTS_STALE_TIME,
      gcTime: PRODUCT_COMMENTS_GC_TIME
    }),
    'product'
  );

interface UseProductCommentsCountsReturn {
  reviewTotalCount: number;
  commentTotalCount: number;
  isProductHasAttachmentInComments: boolean;
  isPending: boolean;
}

/**
 * Счётчики отзывов и вопросов + наличие фото в отзывах — данные для табов
 * карточки товара (BarMenuProduct, позже ProductPage). Кэш счётчиков общий
 * с запросами списков (те же productCommentsQueryOptions).
 */
export const useProductCommentsCountsQuery = (
  catalogId: number
): UseProductCommentsCountsReturn => {
  const enabled = !!catalogId;

  const reviewsCountQuery = useQuery({
    ...productReviewsCountQueryOptions(catalogId),
    enabled
  });

  const commentsCountQuery = useQuery({
    ...productQuestionsCountQueryOptions(catalogId),
    enabled
  });

  const attachmentsQuery = useQuery({
    ...productHasAttachmentInCommentsQueryOptions(catalogId),
    enabled
  });

  return {
    reviewTotalCount: reviewsCountQuery.data?.totalCount || 0,
    commentTotalCount: commentsCountQuery.data?.totalCount || 0,
    isProductHasAttachmentInComments: attachmentsQuery.data ?? false,
    isPending:
      reviewsCountQuery.isPending ||
      commentsCountQuery.isPending ||
      attachmentsQuery.isPending
  };
};

export const PEOPLE_COMMENTS_STATS_QUERY_KEY = 'peopleCommentsStats';

export const peopleCommentsStatsQueryOptions = (
  params: Parameters<typeof API.peopleCommentsStats>[0]
) =>
  queryOptions({
    queryKey: [PEOPLE_COMMENTS_STATS_QUERY_KEY, params],
    queryFn: () => API.peopleCommentsStats(params),
    select: res => res.peopleCommentsStats,
    staleTime: PRODUCT_COMMENTS_STALE_TIME,
    gcTime: PRODUCT_COMMENTS_GC_TIME
  });
